import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { createTheme, useColorScheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { AppProvider } from '@toolpad/core/AppProvider';
import {
  DashboardLayout,
} from '@toolpad/core/DashboardLayout';
import {
  AccountPreview,
} from '@toolpad/core/Account';
import type { Navigation, NavigationItem, Router, Session } from '@toolpad/core/AppProvider';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { PageContainer, PageHeader, PageHeaderToolbar } from '@toolpad/core/PageContainer';
import { Link, useNavigate } from 'react-router';
import { matchPath } from 'react-router-dom';
import axios from 'axios';
import Users from './Administration/Users';
import QRCode from './Administration/QRCode';
import Logs from './Administration/Logs';
import SettingsIcon from '@mui/icons-material/Settings';
import HomePage from './Administration/HomePage';
import Customers from './Administration/Customers';
import invariant from 'invariant';
import Copyright from './Copyright';
import { Avatar, Button, Chip, Drawer, FormControl, FormControlLabel, FormLabel, IconButton, List, ListItem, ListItemButton, Popover, RadioGroup, Tooltip, Radio, InputLabel, Select, MenuItem } from '@mui/material';
import UserAccount from './User/Account';
import LogoutIcon from '@mui/icons-material/Logout';
import { useRef } from 'react';
import { useReactToPrint } from "react-to-print";
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import ErrorBoundary from './Common/ErrorBoundary';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import Settings from './Settings/Settings';
import Whisper from './Administration/Whisper';
import Location from './Administration/Location';
import WhisperIndex from './Whisper/WhisperIndex';
import BarChartIcon from '@mui/icons-material/BarChart';
import PerformanceReviewComponent from './User/PerformanceReview';
import MonthlyInterest from './Whisper/MonthlyInterest';
import MyAccount from './Administration/MyAccount';
import NoAccess from './NoAccess/NoAccess';
import ItemsPurchased from './Administration/Customer/ItemsPurchased';
import InventoryIcon from '@mui/icons-material/Inventory';
import ProductModel from './Administration/ProductModel';
import FadeIn from './FadeIn';

const NAVIGATION: Navigation = [
  {
    kind: 'header',
    title: 'Main items',
  },
  {
    kind: 'page',
    segment: '',
    title: 'Dashboard',
    icon: <DashboardIcon />,
  }
];


if (window.innerWidth < 1200) {
  NAVIGATION.push({
    kind: 'divider',
  });
  NAVIGATION.push({
    kind: 'header',
    title: 'Settings',
  });
  // NAVIGATION.push({
  //   kind: 'page',
  //   segment: 'help',
  //   title: 'Help',
  //   icon: <HelpCenterIcon />,
  // });
}
// if (window.innerWidth < 1200) {
//   NAVIGATION.push({
//     kind: 'page',
//     segment: 'notifications',
//     title: 'Notifications',
//     icon: <NotificationsIcon/>,
//   });
// }

function CustomThemeSwitcher() {
  const { setMode } = useColorScheme();

  const handleThemeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      setMode(event.target.value as 'light' | 'dark' | 'system');
      localStorage.setItem('theme', event.target.value);
      window.location.reload();
    },
    [setMode],
  );

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLElement | null>(null);

  const toggleMenu = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      setMenuAnchorEl(isMenuOpen ? null : event.currentTarget);
      setIsMenuOpen((previousIsMenuOpen) => !previousIsMenuOpen);
    },
    [isMenuOpen],
  );

  return (
    <React.Fragment>
      <Tooltip title="Switch Theme" enterDelay={1000}>
        <div>
          <IconButton type="button" aria-label="settings" onClick={toggleMenu}>
            <SettingsIcon />
          </IconButton>
        </div>
      </Tooltip>
      <Popover
        open={isMenuOpen}
        anchorEl={menuAnchorEl}
        onClose={toggleMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableAutoFocus
      >
        <Box sx={{ p: 2 }}>
          <FormControl>
            <FormLabel id="custom-theme-switcher-label">Theme</FormLabel>
            <RadioGroup
              aria-labelledby="custom-theme-switcher-label"
              defaultValue="system"
              name="custom-theme-switcher"
              value={localStorage.getItem('theme')}
              onChange={handleThemeChange}
            >
              <FormControlLabel value="light" control={<Radio checked={localStorage.getItem('theme') === 'light'} />} label="Light" />
              <FormControlLabel value="system" control={<Radio checked={localStorage.getItem('theme') === 'system'} />} label="System" />
              <FormControlLabel value="dark" control={<Radio checked={localStorage.getItem('theme') === 'dark'} />} label="Dark" />
            </RadioGroup>
          </FormControl>
        </Box>
      </Popover>
    </React.Fragment>
  );
}

const CustomToolbarActions = () => {
  const [state, setState] = React.useState({
    right: false,
  });

  const navigate = useNavigate();
  const toggleDrawer = (anchor: 'right', open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
      return;
    }
    event.preventDefault();
    setState({ ...state, [anchor]: open });
  };

  const logout = async () => {
    const response = await axios.get('/csrf');
    fetch("/logout/", {
      method: "POST",
      headers: {'X-CSRFToken': response.data.csrfToken}
    }
    ).then(response => {
      localStorage.removeItem('user');
      localStorage.removeItem('entity_id');
      localStorage.removeItem('entity_nm');
      window.location.href = "/";
    })
  }
  return (
    <React.Fragment>
      <Stack direction="row" alignItems="center">
        {/* <ThemeSwitcher /> */}
        {/* {window.innerWidth > 1200 && <SidebarFooterAccount mini={false} />} */}
        {/* {window.innerWidth > 1200 && (<React.Fragment>
          <Tooltip title="Help">
            <IconButton disabled>
              <HelpCenterIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Notifications">
            <IconButton disabled>
              <Badge color="secondary" variant="dot">
                <NotificationsIcon />
            </Badge>
            </IconButton>
          </Tooltip>
        </React.Fragment>
        )} */}
        {/* <CustomThemeSwitcher /> */}
        <AccountPreview
          variant="expanded"
          handleClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            setState({ ...state, right: !state['right'] });
          }}
          slots={{
            avatar: () => <Avatar src={user['photo']} sx={{ width: 40, height: 40 }} />,
          }}
          open={false}
          sx={{
            textAlign: 'left',
            '& .MuiTypography-root': { textAlign: 'left' },
          }}
        />
      </Stack>
      <Drawer
        anchor="right"
        open={state['right']} 
        onClose={(event, reason) => {
          toggleDrawer('right', false);
        }}
      >
        <Box
          component="div"
          sx={{ width: 350, marginTop: 10, overflowY: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
          role="presentation"
          onClick={toggleDrawer('right', false)}
          onKeyDown={toggleDrawer('right', false)}
        >
          <List>
            <ListItem>
              <Avatar
                alt={user['name']}
                src={user['photo']}
                sx={{ width: 150, height: 150, margin: 'auto !important' }}
              />
            </ListItem>
            <ListItem>
              <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center', margin: 'auto !important' }}>{user['name']}</Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2" sx={{ textAlign: 'center', margin: 'auto !important' }}>{user['username']}</Typography>
            </ListItem>
            <ListItem>  
              <Typography variant="body2" sx={{ textAlign: 'center', margin: 'auto !important' }}>{user['role'] === 'Host' ? 'IDEA' : localStorage.getItem('entity_nm') || localStorage.getItem('entity_id') || ''}</Typography>
            </ListItem>
            <ListItem>
              {/* <Typography variant="body2" sx={{ textAlign: 'center', margin: 'auto !important' }}>{user['role']}</Typography> */}
              <Stack direction="row" spacing={1} sx={{ margin: 'auto !important' }}>
                <Chip 
                  label={user['role']} 
                  color={user['role'] === 'Administrator' ? 'success' : user['role'] === 'Host' ? 'warning' : 'secondary'} 
                  variant="filled" 
                  sx={{ textTransform: 'capitalize', borderRadius: 2 }}
                />
              </Stack>
            </ListItem>
            <Divider />
            {/* <ListItem style={{ justifyContent: 'center', alignItems: 'center' }}>
              <Tooltip title="Notifications">
                <IconButton disabled>
                  <NotificationsIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Help">
                <IconButton disabled>
                  <HelpCenterIcon />
                </IconButton>
              </Tooltip>
            </ListItem> */}
            <Divider />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}></Box>
            {/* <ListItem disablePadding style={{ justifyContent: 'center', alignItems: 'center' }}>
              {user['role'] === 'Administrator' && (
              <ListItemButton disabled style={{ textAlign: 'center', justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                  <SupervisorAccountIcon />
                  <ListItemText primary="Admin" />
                </Box>
              </ListItemButton>
              )}
            </ListItem> */}
            <ListItem disablePadding style={{ justifyContent: 'center', alignItems: 'center' }}>
              <ListItemButton onClick={() => window.location.href = '/users/' + user['id'] + '/account'} style={{ textAlign: 'center', justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                  <ManageAccountsIcon />
                  <ListItemText primary="Profile" />
                </Box>
              </ListItemButton>
            </ListItem>
          </List>          
        </Box>
        <Box> {/* Add padding and a separator */}
          <Button endIcon={<LogoutIcon />} variant="contained" color="primary" fullWidth onClick={() => logout()} style={{ borderRadius: 0, padding: 10, opacity: 0.8, textTransform: 'capitalize' }}>
            Logout
          </Button>
        </Box>
      </Drawer>
    </React.Fragment>
  );
}

function MainPageContent({ pathname, user, mode, contentRef }: { pathname: string, user: any, mode: string, contentRef: React.RefObject<HTMLDivElement | null> }) {
  const isHost = user['role'] === 'Host';
  const perm = user['role'] === 'Administrator' || user['role'] === 'Host' ? 'admin' : user['role'] === 'Executive' ? 'executive' : '';
  const customerMatchedRoute = matchPath('/customers/:id', pathname);
  const userMatchedRoute = matchPath('/users/:id/account', pathname);
  const settingsMatchedRoute = matchPath('/admin/settings/', pathname);
  const usersMatchedRoute = matchPath('/admin/users/', pathname);
  const logsMatchedRoute = matchPath('/admin/logs/', pathname);
  const locationsMatchedRoute = matchPath('/admin/locations/', pathname);
  const whispersMatchedRoute = matchPath('/admin/whispers/wizard', pathname);
  const whispersIndexMatchedRoute = matchPath(`/${perm}/whispers/`, pathname);
  const performanceReviewMatchedRoute = matchPath(`/${perm}/performance-review/`, pathname);
  const executiveUsersMatchedRoute = matchPath('/executive/users/', pathname);
  // const permissionsMatchedRoute = matchPath('/admin/permissions/', pathname);
  const monthlyInterestsMatchedRoute = matchPath(`/${perm}/whisper/monthly-interests/`, pathname);
  const myAccountsMatchedRoute = matchPath(`/${perm}/my-accounts`, pathname);
  const itemsPurchasedMatchedRoute = matchPath(`/${perm}/customers/:id/items-purchased`, pathname);
  const assignmentsMatchedRoute = matchPath('/admin/users/assignments/', pathname);
  const productModelMatchedRoute = matchPath(`/${perm}/product-model`, pathname);

  return (
    <Box ref={contentRef} style={{ maxWidth: '100% !important', marginTop: isHost ? 0 : -40 }}>
      <FadeIn duration={1000} delay={500}>
        {pathname === '/' && (<HomePage mode={mode} height={345} />)}
        {logsMatchedRoute && (<Logs />)}
        {pathname === '/qrcode/' || pathname === '/qrcode' && (<QRCode />)}
        {pathname === '/no-access' || pathname === '/no-access/' && (<NoAccess />)}
        {(usersMatchedRoute || executiveUsersMatchedRoute) && (<Users loggedInUser={user} />)}
        {settingsMatchedRoute && (<Settings />)}
        {customerMatchedRoute && (<Customers />)}
        {userMatchedRoute && (<UserAccount />)}
        {whispersMatchedRoute && (<Whisper />)}
        {whispersIndexMatchedRoute && (<WhisperIndex />)}
        {locationsMatchedRoute && (<Location />)}
        {performanceReviewMatchedRoute && (<PerformanceReviewComponent height={600} />)}
        {/* {permissionsMatchedRoute && (<Permission />)} */}
        {monthlyInterestsMatchedRoute && (<MonthlyInterest perm={perm} />)}
        {myAccountsMatchedRoute && (<MyAccount />)}
        {itemsPurchasedMatchedRoute && (<ItemsPurchased />)}
        {productModelMatchedRoute && (<ProductModel />)}
        {/* {assignmentsMatchedRoute && (<SalesRepAssignment />)} */}
      </FadeIn>
    </Box>
  );
}

const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : {};

const BRANDING = {
  title: 'IDEA Data Whispers',
  homeUrl: '/',
  logo: <img src='https://idea4industry.com/wp-content/uploads/cropped-IDEA-favicon-192x192.png' alt='IDEA Data Whispers' width='32' height='32' />
}

function CustomAppTitle({ manufacturerName }: { manufacturerName?: string }) {
  const entityName = manufacturerName || localStorage.getItem('entity_nm') || '';
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <img src="https://idea4industry.com/wp-content/uploads/cropped-IDEA-favicon-192x192.png" width="50" height="50" alt="IDEA Data Whispers" />
      <Stack direction="column" alignItems="flex-start" justifyContent="center" spacing={0}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'left', lineHeight: 1.2 }}>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>IDEA Data Whispers</Link>
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'left', color: '#db5f14', lineHeight: 1.2 }}>
          {entityName}
        </Typography>
      </Stack>
    </Stack>
  );
}

function CustomPageToolbar({ status, contentRef, user, setManufacturer, manufacturer }: { status: string, contentRef: React.RefObject<HTMLDivElement | null>, user: any, setManufacturer: (manufacturer: string) => void, manufacturer: string }) {
  const role = user['role'];
  const totalEntities = user['total_entities'];
  const isHost = role === 'Host';
  const entities = user['entities'];
  const reactToPrintContent = useReactToPrint({ contentRef });
  return (
    <PageHeaderToolbar>
      {/* <Tooltip title="Print">
        <IconButton onClick={reactToPrintContent} color="primary" style={{ textTransform: 'capitalize' }}><PrintIcon fontSize="small" /></IconButton>
      </Tooltip>
      <Button startIcon={<FileDownloadIcon />} color="primary" style={{ textTransform: 'capitalize' }}>Export</Button>
      <TextField
          size="small"
          label="Search by customer"
          id="outlined-start-adornment"
          sx={{ m: 1, width: '25ch' }}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            },
          }}
        /> */}
        {(totalEntities > 1 || isHost) && (
          <FormControl fullWidth sx={{ width: '25ch', marginBottom: 10 }}>
            <InputLabel variant="standard" htmlFor="uncontrolled-native">Choose a manufacturer</InputLabel>
            <Select
              size="small"
              variant="standard"
              label="Choose a manufacturer"
              id="manufacturer"
              value={manufacturer || localStorage.getItem('entity_id') || ''}
              onChange={(event) => {
                const selectedId = String(event.target.value || '');
                const selectedEntity = entities.find((entity: any) => String(entity[0]) === selectedId);
                const selectedName = selectedEntity ? String(selectedEntity[1]) : '';
                localStorage.setItem('entity_id', selectedId);
                localStorage.setItem('entity_nm', selectedName);
                const storedUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : {};
                storedUser.entity_id = selectedId;
                storedUser.entity_nm = selectedName;
                localStorage.setItem('user', JSON.stringify(storedUser));
                setManufacturer(selectedId);
              }}
            > 
              {entities.map((entity: any) => (
                <MenuItem key={entity[0]} value={entity[0]}>{entity[1]}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DemoContainer components={['DatePicker', 'DatePicker']}>
          <DatePicker slotProps={{ field: { size: 'small' } as any }} label="Period" defaultValue={dayjs('')} />
        </DemoContainer>
      </LocalizationProvider> */}
    </PageHeaderToolbar>
  );
}

function CustomPageHeader({ status, contentRef, user, setManufacturer, manufacturer }: { status: string, contentRef: React.RefObject<HTMLDivElement | null>, user: any, setManufacturer: (manufacturer: string) => void, manufacturer: string }) {
  const CustomPageToolbarComponent = React.useCallback(
    () => status === '1' && <CustomPageToolbar contentRef={contentRef} status={status} user={user} setManufacturer={setManufacturer} manufacturer={manufacturer} />,
    [status, contentRef, user, setManufacturer, manufacturer],
  );

  return <PageHeader title={''} breadcrumbs={[]} slots={{ toolbar: CustomPageToolbarComponent }} />
}

interface ContentProps {
  router: Router;
  theme: string;
  contentRef: React.RefObject<HTMLDivElement | null>;
  setManufacturer: (manufacturer: string) => void;
  manufacturer: string;
}

function Content({ router, theme, contentRef, setManufacturer, manufacturer }: ContentProps) {
  const pathname = window.location.pathname;
  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : {};
  const mode = localStorage.getItem('theme') || 'light';
  const pageName = pathname.split('/').pop() ?? '';
  
  const activePage = {
    title: pageName.charAt(0).toUpperCase() + pageName.slice(1),
    breadcrumbs: [],
    path: pathname,
  }
  invariant(activePage, 'No navigation match');
  
  const breadcrumbs = [...activePage.breadcrumbs, {
    title: '', //activePage.title,
    path: activePage.path
  }];

  return (
    // preview-start
    <Box sx={{ width: '100%', padding: 5, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
      <PageContainer 
        title={''}
        // title={pathname === '/' ? '' : activePage.title}
        // breadcrumbs={pathname === '/' ? [] : breadcrumbs}
        disableGutters 
        fixed 
        sx={{
          marginBottom: 2,
          '& .MuiTypography-h4': {
            fontSize: pathname === '/' ? '1.2rem' : 'clamp(1rem, 1.5rem, 2rem)',
            fontFamily: 'Trebuchet MS',
          },
        }}
        slots={{
          header: (props: any) => <CustomPageHeader contentRef={contentRef} setManufacturer={setManufacturer} manufacturer={manufacturer} status={pathname === '/' ? '1' : '0'} user={user} {...props} />
        }}
      >
        <MainPageContent key={manufacturer} contentRef={contentRef} pathname={pathname} user={user} mode={theme} />
      </PageContainer>
    </Box>
  );
}

const customTheme = createTheme({
  typography: {
    fontFamily: 'Trebuchet MS',
  },
  // cssVariables: {
  //   colorSchemeSelector: 'data-toolpad-color-scheme',
  // },
  components: {
    MuiTypography: {
      defaultProps: {
        sx: {
          fontSize: 'clamp(0.9rem, 0.9rem, 0.9rem) !important',
          fontFamily: 'Trebuchet MS',
        },
      },
    },
  },
  palette: {
    mode: 'light',
  },
  colorSchemes: {
    light: {
      palette: {
        background: {
          default: '#F9F9FE !important',
          paper: '#F9F9FE !important', 
        },
      },
    },
  },
  //   dark: {
  //     palette: {
  //       background: {
  //         default: '#192841 !important',
  //         paper: '#192841 !important',
  //       },
  //     },
  //   },
  // },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 1200,
      xl: 1536,
    },
  },
});

export default function Dashboard(props: { user: any }) {

  const navigate = useNavigate();
  const [pathname, setPathname] = React.useState('');

  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : {};
  
  const router = React.useMemo<Router>(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path) => {
        setPathname(String(path));
        navigate(String(path));
      },
    };
  }, [pathname]);

  
  const [mode, setMode] = React.useState<string>('light');
  const [session, setSession] = React.useState<Session | null>();

  const customAdminNavigation: NavigationItem[] = NAVIGATION.concat([
    {
      kind: 'header',
      title: 'Admin',
    },
    {
      kind: 'page',
      segment: 'admin/settings',
      title: 'General Settings',
      icon: <SettingsIcon />,
    },
    {
      kind: 'page',
      segment: 'admin/users',
      title: 'Manage Users',
      icon: <ManageAccountsIcon />,
    },
    {
      kind: 'page',
      segment: 'admin/locations',
      title: 'Manage Locations',
      icon: <LocationCityIcon />,
    },
    {
      kind: 'page',
      segment: 'admin/whispers/wizard',
      title: 'Create Whisper',
      icon: <NoteAddIcon />,
    },
  ]);

  const customExecutiveNavigation: NavigationItem[] = NAVIGATION.concat([
    {
      kind: 'page',
      segment: 'executive',
      title: 'Executive',
      icon: <SupervisorAccountIcon />,
    },
  ]);

  const customHostNavigation: NavigationItem[] = NAVIGATION.concat([
    {
      kind: 'page',
      segment: 'qrcode',
      title: 'QR Code',
      icon: <QrCode2Icon />,
    },
    {
      kind: 'page',
      segment: 'logs',
      title: 'Logs',
      icon: <EventNoteIcon />,
    },
  ]);

  const customNavigation: 
  NavigationItem[] = props.user['role'] === 'Host' ? customHostNavigation : 
  props.user['role'] === 'Administrator' ? customAdminNavigation : 
  props.user['role'] === 'Executive' ? customExecutiveNavigation : NAVIGATION;
  

  props.user['role'] === 'Administrator' && customNavigation.push({
    kind: 'page',
    segment: `${props.user['role'] === 'Administrator' ? 'admin' : props.user['role'] === 'Executive' ? 'executive' : ''}/whispers`,
    title: 'Opportunities',
    icon: <FormatListBulletedIcon />,
  });

  props.user['role'] === 'Administrator' && customNavigation.push({
    kind: 'page',
    segment: `${props.user['role'] === 'Administrator' ? 'admin' : props.user['role'] === 'Executive' ? 'executive' : ''}/my-accounts`,
    title: 'My Accounts',
    icon: <Diversity3Icon />,
  });

  props.user['role'] === 'Administrator' && customNavigation.push({
    kind: 'page',
    segment: `${props.user['role'] === 'Administrator' ? 'admin' : props.user['role'] === 'Executive' ? 'executive' : ''}/performance-review`,
    title: 'Performance Review',
    icon: <BarChartIcon />,
  });

  props.user['role'] === 'Administrator' && customNavigation.push({
    kind: 'page',
    segment: `${props.user['role'] === 'Administrator' ? 'admin' : props.user['role'] === 'Executive' ? 'executive' : ''}/product-model`,
    title: 'Product Model',
    icon: <InventoryIcon />,
  });

  const [manufacturer, setManufacturer] = React.useState(localStorage.getItem('entity_id') || '');
  const manufacturerName = React.useMemo(() => {
    const entities = props.user?.entities || [];
    const selectedEntity = entities.find((entity: any) => String(entity[0]) === String(manufacturer));
    return selectedEntity ? String(selectedEntity[1]) : (localStorage.getItem('entity_nm') || '');
  }, [manufacturer, props.user]);

  const authentication = React.useMemo(() => {
    return {
      user: session,
      signIn: () => {
        setSession({'user': {'name': props.user['name'], 'email': props.user['username'], 'image': props.user['photo']}});
      },
      signOut: async () => {
          const response = await axios.get('/csrf');
          fetch("/logout/", {
              method: "POST",
              headers: {'X-CSRFToken': response.data.csrfToken}
            }
          ).then(response => {
            localStorage.removeItem('user');
            localStorage.removeItem('entity_id');
            localStorage.removeItem('entity_nm');
            window.location.href = "/";
          })
        }
    };
  }, [session]);


  React.useEffect(() => {

    setPathname(window.location.pathname);
    setTimeout(() => {
      setSession({'user': {'name': props.user['name'], 'email': props.user['username'], 'image': props.user['photo']}});
    }, 100);


    // Forcing light theme view
    setMode('light');

  }, [window.location.pathname, manufacturer]);


  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintContent = useReactToPrint({ contentRef });

  return (
    <AppProvider
      branding={BRANDING}
      navigation={customNavigation}
      router={router}
      authentication={authentication}
      session={session}
      theme={customTheme}
    >
      <DashboardLayout
        sidebarExpandedWidth={220}
        defaultSidebarCollapsed={window.innerWidth < 1200 ? true : false}
        slots={{
          appTitle: () => <CustomAppTitle manufacturerName={manufacturerName} />,
          sidebarFooter: Copyright,
          toolbarActions: CustomToolbarActions
        }}
      >
        <ErrorBoundary>
          <Content contentRef={contentRef} router={router} setManufacturer={setManufacturer} manufacturer={manufacturer} theme={localStorage.getItem('theme') || 'light'} />
        </ErrorBoundary>
      </DashboardLayout>
    
    </AppProvider>
  );

}

